import { getTokenOrRefresh } from '../token_util';
import { getSttEngineConfig } from './sttEngine';
import type ReactAudioPlayer from 'react-audio-player';

export interface AvatarRef {
  playIdleAnimation: () => void;
  stopVisemeAnimation?: () => void;
}

/**
 * Audio playback state tracking for animation synchronization
 */
export interface AudioPlaybackState {
  isPlaying: boolean;
  isPaused: boolean;
  isEnded: boolean;
  currentTime: number;
  duration: number;
  startTime: number | null; // Timestamp when playback started
  lastUpdateTime: number;
}

/**
 * Global audio state tracker
 */
let globalAudioState: AudioPlaybackState = {
  isPlaying: false,
  isPaused: false,
  isEnded: false,
  currentTime: 0,
  duration: 0,
  startTime: null,
  lastUpdateTime: Date.now()
};

async function loadSpeechSdk() {
  return import('microsoft-cognitiveservices-speech-sdk');
}

/**
 * Get current audio playback state
 */
export function getAudioPlaybackState(): Readonly<AudioPlaybackState> {
  return { ...globalAudioState };
}

/**
 * Update audio playback state
 */
export function updateAudioPlaybackState(updates: Partial<AudioPlaybackState>): void {
  globalAudioState = {
    ...globalAudioState,
    ...updates,
    lastUpdateTime: Date.now()
  };
}

/**
 * Check if audio is actually playing (not just events)
 */
export function isAudioActuallyPlaying(): boolean {
  return globalAudioState.isPlaying && !globalAudioState.isPaused && !globalAudioState.isEnded;
}

/**
 * Reset audio state
 */
export function resetAudioState(): void {
  globalAudioState = {
    isPlaying: false,
    isPaused: false,
    isEnded: false,
    currentTime: 0,
    duration: 0,
    startTime: null,
    lastUpdateTime: Date.now()
  };
}

/**
 * Monitor audio element for actual playback state
 */
export function monitorAudioElement(audioElement: HTMLAudioElement | null, onStateChange?: (state: AudioPlaybackState) => void): () => void {
  if (!audioElement) {
    return () => {}; // No-op cleanup
  }

  const updateState = () => {
    const newState: Partial<AudioPlaybackState> = {
      isPlaying: !audioElement.paused && !audioElement.ended,
      isPaused: audioElement.paused,
      isEnded: audioElement.ended,
      currentTime: audioElement.currentTime,
      duration: audioElement.duration || 0
    };

    if (newState.isPlaying && !globalAudioState.isPlaying && !globalAudioState.startTime) {
      newState.startTime = Date.now();
    }

    if (newState.isEnded) {
      newState.isPlaying = false;
      newState.isPaused = false;
    }

    updateAudioPlaybackState(newState);
    if (onStateChange) {
      onStateChange(getAudioPlaybackState());
    }
  };

  // Listen to all relevant events
  audioElement.addEventListener('play', updateState);
  audioElement.addEventListener('pause', updateState);
  audioElement.addEventListener('ended', updateState);
  audioElement.addEventListener('timeupdate', updateState);
  audioElement.addEventListener('loadedmetadata', updateState);

  // Initial state
  updateState();

  // Return cleanup function
  return () => {
    audioElement.removeEventListener('play', updateState);
    audioElement.removeEventListener('pause', updateState);
    audioElement.removeEventListener('ended', updateState);
    audioElement.removeEventListener('timeupdate', updateState);
    audioElement.removeEventListener('loadedmetadata', updateState);
  };
}

/**
 * Engine dispatcher. Azure (below, the historical default) unless the
 * host selected the Camlin Speech ASR engine via `configureSttEngine`
 * (sttEngine.ts) — e.g. the Companion widget's site-config `asr` block.
 * Every mic surface above this call (live listen, Companion mini-mic)
 * follows the selection without knowing about it.
 */
export async function sttFromMic(
  setDisplayText: (text: string) => void,
  setUserTalking: (value: boolean) => void,
  setRecord: (value: boolean) => void,
  setSpeak: (value: boolean) => void,
  askCamlin: (text: string) => void,
  sendMsgToBot: (msg: string, direction: string) => void,
  onInterimTranscript?: (text: string) => void
) {
  if (getSttEngineConfig().engine === 'camlin') {
    const { sttFromMicCamlin } = await import('./camlinAsr');
    return sttFromMicCamlin(
      setDisplayText,
      setUserTalking,
      setRecord,
      setSpeak,
      askCamlin,
      sendMsgToBot,
      onInterimTranscript
    );
  }
  return sttFromMicAzure(
    setDisplayText,
    setUserTalking,
    setRecord,
    setSpeak,
    askCamlin,
    sendMsgToBot,
    onInterimTranscript
  );
}

export async function sttFromMicAzure(
  setDisplayText: (text: string) => void,
  setUserTalking: (value: boolean) => void,
  setRecord: (value: boolean) => void,
  setSpeak: (value: boolean) => void,
  askCamlin: (text: string) => void,
  sendMsgToBot: (msg: string, direction: string) => void,
  onInterimTranscript?: (text: string) => void
) {
  try {
    setDisplayText('Initializing audio...');
    if (window.avatarAudioContext) {
      await window.avatarAudioContext.close();
      window.avatarAudioContext = null;
    }
    // Reuse the cached stream from `useVoiceBrowserAccess.ensureVoice
    // BrowserAccess` if it's still live. The Listen button grants
    // microphone permission and caches the resulting MediaStream as
    // `window.camlinSharedAudioStream`; reusing it here avoids a second
    // getUserMedia call that some browsers re-prompt for or fail in
    // locked-down corporate environments. See useVoiceBrowserAccess
    // header for full rationale (Keith report 2026-04-23).
    let stream: MediaStream | null = null;
    const sharedAudioStream = (window as unknown as { camlinSharedAudioStream?: MediaStream | null }).camlinSharedAudioStream;
    const sharedAudioStreamLive = sharedAudioStream
      && sharedAudioStream.active
      && sharedAudioStream.getAudioTracks().some((track) => track.readyState === 'live' && !track.muted);
    if (sharedAudioStreamLive && sharedAudioStream) {
      console.log('[sttFromMic] Reusing cached audio stream from Listen-button permission grant');
      stream = sharedAudioStream;
    } else {
      if (window.currentAudioStream && window.currentAudioStream !== sharedAudioStream) {
        window.currentAudioStream.getTracks().forEach(track => track.stop());
        window.currentAudioStream = null;
      }
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1
        }
      });
      // Remember the freshly-acquired stream as the shared one too —
      // any subsequent Listen press will see it and skip the prompt.
      (window as unknown as { camlinSharedAudioStream?: MediaStream | null }).camlinSharedAudioStream = stream;
    }
    window.currentAudioStream = stream;
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    const audioContext = new AudioContext({ sampleRate: 44100 });
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
    const source = audioContext.createMediaStreamSource(stream);
    const gainNode = audioContext.createGain();
    gainNode.gain.value = 0;
    source.connect(gainNode);
    gainNode.connect(audioContext.destination);
    window.avatarAudioContext = audioContext;
    console.log('Audio initialized successfully');
    setDisplayText('Getting speech recognition ready...');
    const tokenObj = await getTokenOrRefresh();
    if (!tokenObj?.authToken || !tokenObj?.region) {
      throw new Error(
        typeof tokenObj?.error === 'string' && tokenObj.error.trim()
          ? tokenObj.error
          : 'Failed to get speech token or region'
      );
    }
    const { ResultReason, AudioConfig, SpeechRecognizer, SpeechConfig } = await loadSpeechSdk();
    const speechConfig = SpeechConfig.fromAuthorizationToken(tokenObj.authToken, tokenObj.region);
    const audioConfig = AudioConfig.fromDefaultMicrophoneInput();
    const recognizer = new SpeechRecognizer(speechConfig, audioConfig);
    setDisplayText('Speak now!');
    setUserTalking(true);
    setRecord(true);

    const cleanup = async () => {
      setUserTalking(false);
      setSpeak(false);
      setRecord(false);
      if (window.currentAudioStream) {
        window.currentAudioStream.getTracks().forEach(track => track.stop());
        window.currentAudioStream = null;
      }
      recognizer.close();
      if (window.avatarAudioContext) {
        await window.avatarAudioContext.close();
        window.avatarAudioContext = null;
      }
    };

    if (onInterimTranscript) {
      let silenceTimer: ReturnType<typeof setTimeout> | null = null;
      const SILENCE_TIMEOUT_MS = 3000;

      const resetSilenceTimer = () => {
        if (silenceTimer) clearTimeout(silenceTimer);
        silenceTimer = setTimeout(async () => {
          try { await recognizer.stopContinuousRecognitionAsync(); } catch {}
        }, SILENCE_TIMEOUT_MS);
      };

      recognizer.recognizing = (_sender: any, e: any) => {
        const partialText = e.result?.text?.trim() || '';
        if (partialText) {
          onInterimTranscript(partialText);
          setDisplayText(partialText);
          resetSilenceTimer();
        }
      };

      recognizer.recognized = async (_sender: any, e: any) => {
        if (silenceTimer) clearTimeout(silenceTimer);
        if (e.result.reason === ResultReason.RecognizedSpeech) {
          const text = e.result.text.trim().replace(/[.!?]+$/, '');
          if (text) {
            setDisplayText(`Processing: ${text}`);
            await askCamlin(text);
            sendMsgToBot(text, 'out');
            setDisplayText(`You said: ${text}`);
          }
        }
        try { await recognizer.stopContinuousRecognitionAsync(); } catch {}
      };

      recognizer.canceled = async () => {
        if (silenceTimer) clearTimeout(silenceTimer);
        await cleanup();
      };

      recognizer.sessionStopped = async () => {
        if (silenceTimer) clearTimeout(silenceTimer);
        await cleanup();
      };

      resetSilenceTimer();
      await recognizer.startContinuousRecognitionAsync();
    } else {
      recognizer.recognized = (_sender: any, e: any) => {
        console.log('Recognition event received:', e);
      };
      recognizer.recognizeOnceAsync(async (result: any) => {
        try {
          console.log('Recognition result:', result);
          if (result.reason === ResultReason.RecognizedSpeech) {
            const text = result.text.trim().replace(/[.!?]+$/, '');
            if (text) {
              setDisplayText(`Processing: ${text}`);
              await askCamlin(text);
              sendMsgToBot(text, 'out');
              setDisplayText(`You said: ${text}`);
            } else {
              setDisplayText('No speech detected. Please try again.');
            }
          } else {
            console.log('Recognition failed:', result.reason);
            setDisplayText('Speech not recognized. Please try again.');
          }
        } catch (error: any) {
          console.error('Recognition error:', error);
          setDisplayText('Error processing speech. Please try again.');
        } finally {
          await cleanup();
        }
      });
    }
  } catch (error: any) {
    console.error('sttFromMic error:', error);
    let errorMessage = 'Error accessing microphone. Please check permissions and try again.';
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      errorMessage = 'Microphone access was denied. Please allow microphone access and try again.';
    } else if (error.name === 'NotFoundError') {
      errorMessage = 'No microphone found. Please check your microphone connection.';
    } else if (error.name === 'NotReadableError') {
      errorMessage = 'Microphone is busy or not readable. Please try again.';
    } else if (
      typeof error?.message === 'string' &&
      (error.message.includes('speech token') || error.message.includes('region'))
    ) {
      errorMessage = 'Speech recognition is unavailable right now. Please try again later.';
    }
    setDisplayText(errorMessage);
    setUserTalking(false);
    setSpeak(false);
    setRecord(false);
    if (window.currentAudioStream) {
      window.currentAudioStream.getTracks().forEach(track => track.stop());
      window.currentAudioStream = null;
    }
    if (window.avatarAudioContext) {
      await window.avatarAudioContext.close();
      window.avatarAudioContext = null;
    }
    throw error;
  }
}

export function playerEnded(setAudioSource: (source: string | null) => void, setSpeak: (value: boolean) => void, setPlaying: (value: boolean) => void, setRecord: (value: boolean) => void, avatarRef: React.RefObject<AvatarRef>) {
  try {
    // Update audio state to reflect actual end
    updateAudioPlaybackState({
      isPlaying: false,
      isPaused: false,
      isEnded: true,
      currentTime: 0
    });

    setAudioSource(null);
    setSpeak(false);
    setPlaying(false);
    setRecord(false);

    // Stop any active viseme animation when audio ends
    // This is safe because audio has actually ended
    if (avatarRef.current && avatarRef.current.stopVisemeAnimation) {
      avatarRef.current.stopVisemeAnimation();
    }

    if (avatarRef.current && avatarRef.current.playIdleAnimation) {
      avatarRef.current.playIdleAnimation();
    }
    console.log('Audio playback ended - stopped viseme animation');

    // Notify parent window that speech has completed
    // This is used by the Test Theater to know when to advance to the next step
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'avatar.speechComplete' }, '*');
      console.log('✅ Sent avatar.speechComplete to parent');
    }
  } catch (error: any) {
    console.error('Error in playerEnded:', error);
  }
}

/**
 * Detect whether an audio playback rejection was caused by the browser's
 * autoplay policy. NotAllowedError is the canonical Chrome / Edge / Firefox
 * shape; Safari uses NotAllowedError too but has historically thrown plain
 * Errors with `Notification request denied` / `gesture` substrings, hence the
 * defensive message check. SecurityError is documented for Webview surfaces
 * (older iOS Safari WKWebView) when audio playback is blocked by the host
 * embedder.
 *
 * Returning true here is the trigger for surfacing the AudioPermissionChip
 * (in NativeLiveAvatar) or BrowserAccessPromptModal (in App.tsx) via the
 * `avatar:audio-access-required` window event — see Slice 8 of the avatar
 * fixes plan, May 11 2026.
 */
export function isAutoplayBlockedError(error: unknown): boolean {
  if (!error) return false;
  const errorName = error instanceof DOMException ? error.name : '';
  if (errorName === 'NotAllowedError' || errorName === 'SecurityError') {
    return true;
  }
  const message = (() => {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    if (typeof error === 'object' && error !== null && 'message' in error) {
      const msg = (error as { message?: unknown }).message;
      return typeof msg === 'string' ? msg : '';
    }
    return '';
  })();
  if (!message) return false;
  const normalized = message.toLowerCase();
  return (
    normalized.includes('user didn') ||
    normalized.includes('user gesture') ||
    normalized.includes('autoplay') ||
    normalized.includes('not allowed') ||
    normalized.includes('user activation')
  );
}

/**
 * Dispatch the `avatar:audio-access-required` window event so the SDK's
 * permission surfaces (AudioPermissionChip, BrowserAccessPromptModal) and the
 * health badge (`useAvatarHealth`) can react. Idempotent — multiple dispatches
 * within the same blocked window are coalesced by the consuming hook
 * (`useVoiceBrowserAccess`) which only sets state when transitioning from
 * "no prompt" to "prompt visible".
 */
export function notifyAudioAccessRequired(reason: string): void {
  if (typeof window === 'undefined') return;
  console.warn('[audioUtils] Audio playback blocked, requesting access:', reason);
  window.dispatchEvent(new CustomEvent('avatar:audio-access-required', {
    detail: { message: 'Audio playback is blocked for this page. Allow audio playback in the browser, then retry access or press Listen again.' }
  }));
}

export function playerReady(audioPlayer: React.RefObject<ReactAudioPlayer>, setPlaying: (value: boolean) => void) {
  const play = async () => {
    const audioEl = audioPlayer.current?.audioEl.current;
    if (!audioEl) {
      return;
    }

    await audioEl.play();
    setPlaying(true);
    // Update audio state when playback actually starts
    updateAudioPlaybackState({
      isPlaying: true,
      isPaused: false,
      isEnded: false,
      startTime: Date.now()
    });
  };

  void play().catch((error: any) => {
    console.error('Error in playerReady:', error);
    setPlaying(false);
    updateAudioPlaybackState({
      isPlaying: false,
      isPaused: false,
      isEnded: false
    });
    // Surface the permission chip/modal whenever the browser's autoplay
    // policy rejects us. Pre-Slice-8 this rejection was logged but never
    // surfaced — the user saw the avatar's mouth animate via the 4-second
    // fallback in `useLivePromptPlayback` while no audio played, which on
    // AgentLink and DecisionMemory looked like the avatar was broken
    // rather than waiting on a permission gesture.
    if (isAutoplayBlockedError(error)) {
      notifyAudioAccessRequired('playerReady audioEl.play() rejected');
    }
  });

  console.log('Audio player ready');
}
